// The one diff renderer for every agent-state and prompt diff surface.
//
// A purely-additive change (every first state document) opens as rendered
// markdown, with the diff a click away; a change with removals opens on the diff,
// where the +/- lines are the point. Lines fold under their markdown headings.
import { useState } from "react";

import type { AgentStateDiffLine } from "../api/models/agentState";
import { Markdown } from "./Markdown";
import { isTextSelectionClick, SELECTABLE_LABEL_STYLE } from "./selectableButton";
import styles from "./DiffView.module.css";
import { Chevron } from "./Chevron";

const MARKER: Record<AgentStateDiffLine["kind"], string> = {
  added: "+",
  removed: "-",
  hunk: "@",
  context: " ",
};

// `@@ -0,0 +1,114 @@` in English: new material or a rewrite, and how much.
const HUNK_RE = /^@@\s*-(\d+)(?:,(\d+))?\s+\+(\d+)(?:,(\d+))?\s*@@/;

const describeHunk = (text: string): string | null => {
  const m = HUNK_RE.exec(text);
  if (!m) return null;
  const removed = m[2] === undefined ? 1 : Number(m[2]);
  const addedFrom = Number(m[3]);
  const added = m[4] === undefined ? 1 : Number(m[4]);
  const lines = (n: number) => `${n} line${n === 1 ? "" : "s"}`;
  if (removed === 0) {
    return addedFrom <= 1 ? `New - ${lines(added)}` : `Added at line ${addedFrom}`;
  }
  if (added === 0) return `Removed ${lines(removed)}`;
  return `Changed around line ${addedFrom}`;
};

// Fenced code blocks are NOT tracked (a fence opener can sit outside the hunk), so
// a "# " line inside one costs a spurious fold and nothing else.
const HEADING_RE = /^(#{1,6})\s+(.+?)\s*#*\s*$/;

interface DiffSection {
  heading: AgentStateDiffLine;
  level: number;
  title: string;
  lines: AgentStateDiffLine[];
}

const groupByHeading = (
  lines: readonly AgentStateDiffLine[],
): { lead: AgentStateDiffLine[]; sections: DiffSection[] } => {
  const lead: AgentStateDiffLine[] = [];
  const sections: DiffSection[] = [];
  for (const line of lines) {
    const heading = line.kind === "hunk" ? null : HEADING_RE.exec(line.text);
    if (heading) {
      sections.push({
        heading: line,
        level: heading[1].length,
        title: heading[2],
        lines: [],
      });
    } else if (sections.length === 0) {
      lead.push(line);
    } else {
      sections[sections.length - 1].lines.push(line);
    }
  }
  return { lead, sections };
};

const countChanges = (section: DiffSection): { added: number; removed: number } => {
  let added = section.heading.kind === "added" ? 1 : 0;
  let removed = section.heading.kind === "removed" ? 1 : 0;
  for (const line of section.lines) {
    if (line.kind === "added") added += 1;
    else if (line.kind === "removed") removed += 1;
  }
  return { added, removed };
};

const DiffLines = ({ lines }: { lines: readonly AgentStateDiffLine[] }) => (
  <pre className={styles.lines}>
    {lines.map((line, i) => {
      if (line.kind === "hunk") {
        const described = describeHunk(line.text);
        if (described) {
          return (
            <div key={i} className={styles.hunkLabel}>
              {described}
            </div>
          );
        }
      }
      return (
        <div key={i} className={`${styles.line} ${styles[`kind_${line.kind}`] ?? ""}`}>
          <span className={styles.marker}>{MARKER[line.kind]}</span>
          {line.text || " "}
        </div>
      );
    })}
  </pre>
);

// Removals are dropped: this view is only the default when there are none.
const toMarkdown = (lines: readonly AgentStateDiffLine[]): string =>
  lines
    .filter((l) => l.kind === "added" || l.kind === "context")
    .map((l) => l.text)
    .join("\n");

const hasRemovals = (lines: readonly AgentStateDiffLine[]): boolean =>
  lines.some((l) => l.kind === "removed");

// A <button> for keyboard access; the selectable-label style and drag guard let
// an operator copy a section title without folding it.
const Section = ({ section }: { section: DiffSection }) => {
  const [open, setOpen] = useState(true);
  const { added, removed } = countChanges(section);
  return (
    <div className={styles.section}>
      <button
        type="button"
        className={`${styles.header} ${styles[`kind_${section.heading.kind}`] ?? ""}`}
        style={SELECTABLE_LABEL_STYLE}
        aria-expanded={open}
        onClick={(e) => {
          if (isTextSelectionClick(e)) return;
          setOpen(!open);
        }}
      >
        <span className={styles.caret} aria-hidden="true">
          <Chevron tree open={open} />
        </span>
        <span className={styles.marker}>{MARKER[section.heading.kind]}</span>
        <span className={styles.title} data-level={section.level}>
          {section.title}
        </span>
        {(added > 0 || removed > 0) && (
          <span className={styles.counts}>
            {added > 0 && <span className={styles.countAdded}>+{added}</span>}
            {removed > 0 && <span className={styles.countRemoved}>-{removed}</span>}
          </span>
        )}
      </button>
      {open && section.lines.length > 0 && <DiffLines lines={section.lines} />}
    </div>
  );
};

export const DiffView = ({ lines }: { lines: readonly AgentStateDiffLine[] }) => {
  const removals = hasRemovals(lines);
  const [asDiff, setAsDiff] = useState(removals);
  if (lines.length === 0) {
    return <p className={styles.empty}>No difference.</p>;
  }
  const { lead, sections } = groupByHeading(lines);
  return (
    <div className={styles.frame}>
      <div className={styles.viewRow}>
        <button
          type="button"
          className={styles.viewToggle}
          onClick={() => setAsDiff(!asDiff)}
          aria-pressed={asDiff}
        >
          {asDiff ? "Show as document" : "Show what changed"}
        </button>
        {!removals && (
          <span className={styles.viewNote}>Nothing is being removed - this is all new text.</span>
        )}
      </div>
      {!asDiff ? (
        <div className={styles.rendered}>
          <Markdown>{toMarkdown(lines)}</Markdown>
        </div>
      ) : (
        <>
          {(sections.length === 0 || lead.length > 0) && (
            <DiffLines lines={sections.length === 0 ? lines : lead} />
          )}
          {sections.map((section, i) => (
            <Section key={`${i}-${section.title}`} section={section} />
          ))}
        </>
      )}
    </div>
  );
};
